/**
 * Navigation Configuration
 *
 * Single source of truth for all navigation links.
 * Used by Navbar and Footer to ensure consistency.
 */

export interface NavLink {
  label: string;
  href: string;
  isRoute?: boolean;
}

/**
 * Primary navigation links - hash-based sections on homepage
 * Order matters: this is the canonical order for navbar and footer
 */
export const navLinks: NavLink[] = [
  { label: "Services", href: "#services" },
  { label: "How we work", href: "#how-we-work" },
  { label: "Work", href: "#work" },
  { label: "Pricing", href: "/pricing", isRoute: true },
  { label: "About", href: "#about" },
  { label: "FAQs", href: "#faqs" },
];

/**
 * Footer-only links that appear after primary navigation
 */
export const footerExtraLinks: NavLink[] = [
  { label: "Website Design", href: "/website-design", isRoute: true },
  { label: "Contact", href: "/contact", isRoute: true },
];

/**
 * Combined navigation for footer (primary + extra)
 */
export const footerNavLinks: NavLink[] = [...navLinks, ...footerExtraLinks];

/**
 * Legal/utility links for footer bottom
 */
export const legalLinks: NavLink[] = [
  { label: "Terms of Service", href: "/terms-of-service", isRoute: true },
  { label: "Privacy Policy", href: "/privacy-policy", isRoute: true },
];

/**
 * Social media links — shown in the footer's Social Media column.
 * The Facebook entry is a facebook.com/share/ link (a share-action URL, not
 * the Page's permanent identity) and must never be used in sameAs/schema —
 * see googleBusinessProfileUrl and schemaSafeSameAs() in lib/schema.ts.
 * Instagram and LinkedIn are canonical profile/company URLs, so both are
 * safe to publish in schema sameAs.
 */
export const socialLinks = [
  { label: "Facebook", href: "https://www.facebook.com/profile.php?id=61593565420218" },
  {
    label: "Instagram",
    // Canonical profile URL, no share-tracking query params (those were
    // Instagram's own qr/igsi attribution tags from a copied share link —
    // meaningless once posted elsewhere, and an HTML-encoded "&" in an
    // href, though valid, was tripping naive link checkers that don't
    // decode entities before requesting the URL).
    href: "https://www.instagram.com/coreshift.co.za/",
  },
  {
    label: "LinkedIn",
    // Canonical company-page URL — safe in schema sameAs.
    href: "https://www.linkedin.com/company/coreshift-services",
  },
];

/** Google Business Profile — shown in the footer and reused in schema sameAs. */
export const googleBusinessProfileUrl = "https://share.google/OATXvduQPwsIjopm9";

/**
 * Where Coreshift is based. Read by the Contact page map and by the
 * ProfessionalService schema (lib/schema.ts), so the two never disagree.
 * There's no official street address: `mapArea` only centres the map (it is
 * not shown as text or published as an address), and `geo` is the
 * approximate centre of the Mangold Park area, published as schema.org
 * GeoCoordinates (from Google Maps: right-click the spot → the first menu
 * item is "lat, lng").
 */
export const businessLocation = {
  mapArea: "Mangold Park",
  city: "Gqeberha",
  formerCityName: "Port Elizabeth",
  region: "Eastern Cape",
  countryCode: "ZA",
  geo: { latitude: -33.961119, longitude: 25.559872 } as { latitude: number; longitude: number } | null,
};

const mapQuery = encodeURIComponent(
  `${businessLocation.mapArea}, ${businessLocation.city}, ${businessLocation.region}, South Africa`,
);

/** Keyless Google Maps embed + a normal Maps link for the same place. */
export const businessMapEmbedUrl = `https://www.google.com/maps?q=${mapQuery}&output=embed`;
export const businessMapUrl = `https://www.google.com/maps/search/?api=1&query=${mapQuery}`;
